/**
 * Pin-wrap images are photos drawn inside a map pin.
 *
 * Mapbox can use a photo as a symbol only after the client registers it with
 * `addImage`. The image service does that preparation at
 * `/public/app-main/image/pinWrapImage/<id>`: it circle-crops the stored file
 * and composites it onto a pin.
 *
 * Request that URL only for an id the symbol layer will actually draw:
 *
 * - a custom `markerImage` on a standard marker
 * - a partner marker's `image` (the icon falls back to that photo)
 * - story-marker, signal, and current-location avatars
 *
 * A normal marker's `image` is the content photo (the picture on the marker
 * record). The pin on the map is the marker-type SVG (`fishingLocationMarker`
 * and the rest), registered in `onInitializeStandardMarker`. Fetching a pin
 * wrap for that photo hits the image service for a file the map never draws.
 * Many of those files are missing, so the request 404s.
 *
 * A confirmed 404 is remembered for the page session. `map.hasImage` stays
 * false after a miss, and the map reloads markers on every move, so without
 * this set the same missing id is fetched again on the next pan. Network
 * errors, timeouts, and other HTTP statuses are not remembered, so the next
 * move retries them.
 */

const failedPinWrapImageIds = new Set<string>();

/** Mapbox reports an HTTP miss as an error with `status` 404. */
const isConfirmedMissingImage = (error: unknown): boolean =>
  !!error &&
  typeof error === 'object' &&
  (error as { status?: unknown }).status === 404;
const pinWrapLoadsInFlight = new Map<string, Promise<boolean>>();

export interface StandardMarkerPinSource {
  image?: string | null;
  markerImage?: string | null;
  partner?: unknown;
}

export interface PinWrapMap {
  hasImage: (id: string) => boolean;
  loadImage: (
    url: string,
    callback: (error?: Error | null, image?: unknown) => void,
  ) => void;
  addImage: (id: string, image: unknown) => void;
}

export const pinWrapImageUrl = (imageId: string): string =>
  `/public/app-main/image/pinWrapImage/${imageId}`;

/**
 * Ids the standard-marker layer will draw as a pin-wrap photo.
 *
 * Icon selection matches `marker-cache`: custom `markerImage`, then a
 * partner's `image`, then the marker-type SVG. Only the first two are photos.
 */
export const standardMarkerPinWrapImageIds = (
  markers: StandardMarkerPinSource[],
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  const add = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };
  markers.forEach((marker) => {
    if (marker.markerImage) {
      add(marker.markerImage);
      return;
    }
    if (marker.partner && marker.image) {
      add(marker.image);
    }
  });
  return ids;
};

/** Clears the session failure list. Used by tests. */
export const clearFailedPinWrapImageIds = (): void => {
  failedPinWrapImageIds.clear();
  pinWrapLoadsInFlight.clear();
};

const loadPinWrapImage = (
  map: PinWrapMap,
  imageId: string,
): Promise<boolean> => {
  if (failedPinWrapImageIds.has(imageId)) return Promise.resolve(true);
  if (map.hasImage(imageId)) return Promise.resolve(false);

  const inFlight = pinWrapLoadsInFlight.get(imageId);
  if (inFlight) return inFlight;

  let resolvePending: (failed: boolean) => void = () => {};
  const pending = new Promise<boolean>((resolve) => {
    resolvePending = resolve;
  });
  // Register before loadImage. The callback can run synchronously.
  pinWrapLoadsInFlight.set(imageId, pending);

  map.loadImage(pinWrapImageUrl(imageId), (error, image) => {
    pinWrapLoadsInFlight.delete(imageId);
    if (error || image == null) {
      if (isConfirmedMissingImage(error)) {
        failedPinWrapImageIds.add(imageId);
      }
      resolvePending(true);
      return;
    }
    if (!map.hasImage(imageId)) {
      map.addImage(imageId, image);
    }
    resolvePending(false);
  });

  return pending;
};

/**
 * Loads pin-wrap photos onto the map.
 *
 * Returns the ids that failed this call. Confirmed 404s are also returned on
 * later calls without a new request. Other failures are returned for this
 * call only, so signal markers can use a fallback icon while the next move
 * retries.
 */
export const loadPinWrapImages = async (
  map: PinWrapMap,
  imageIds: Array<string | null | undefined>,
): Promise<string[]> => {
  const uniqueIds: string[] = [];
  const seen = new Set<string>();
  imageIds.forEach((imageId) => {
    if (!imageId || seen.has(imageId)) return;
    seen.add(imageId);
    uniqueIds.push(imageId);
  });
  const failed = await Promise.all(
    uniqueIds.map((imageId) => loadPinWrapImage(map, imageId)),
  );
  return uniqueIds.filter((_, index) => failed[index]);
};
